import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, trackEvent } from '../services/firebase';
import Container from './Container';
import Section from './Section';

/**
 * SocialWall — an interactive "building in public" wall of the brand's OWN real
 * posts that actually shipped to X. Nothing here is invented: it reads
 * `social_posts` where `postedToX === true`, so a card only appears once the
 * post is genuinely live on X. If there are no real posts yet, the whole
 * section renders nothing (same honesty rule as the AI-news section on Home).
 *
 * Design language borrows the modern startup-landing aesthetic (glassmorphism,
 * teal glow, motion) layered on the site's navy/teal system. The ambient
 * background is a Higgsfield-generated image, gently animated with CSS (no
 * autoplay video — lighter, and it respects prefers-reduced-motion).
 */

// account slug / platform -> public X identity. Handles verified from
// x_ai_automation/README.md.
const ACCOUNTS = {
  main:       { handle: 'techtonic190427', name: 'Byamba Enkhbat', avatar: '/byamba.jpg' },
  twitter:    { handle: 'techtonic190427', name: 'Byamba Enkhbat', avatar: '/byamba.jpg' },
  techtonic:  { handle: 'techtonic190427', name: 'Byamba Enkhbat', avatar: '/byamba.jpg' },
  coderjs:    { handle: 'coderjs1',        name: 'CoderJS' },
  mongol:     { handle: 'enkhbatbyambats', name: 'Byamba Enkhbat' },
};
const DEFAULT_ACCOUNT = ACCOUNTS.main;

// Only surface the primary brand account on the wall.
const WALL_HANDLE = 'techtonic190427';

// Deterministic teal/navy gradient per handle for the monogram avatar — no
// generated face photos, just clean branded initials.
const AVATAR_GRADIENTS = [
  'from-teal-400 to-cyan-600',
  'from-sky-400 to-indigo-600',
  'from-emerald-400 to-teal-600',
  'from-violet-400 to-purple-600',
];

function accountFor(post) {
  return ACCOUNTS[post.platform] || DEFAULT_ACCOUNT;
}

function permalink(post) {
  const acct = accountFor(post);
  if (post.url && /(?:twitter|x)\.com/i.test(post.url)) return post.url;
  if (post.tweetId) return `https://x.com/${acct.handle}/status/${post.tweetId}`;
  return `https://x.com/${acct.handle}`;
}

function initialsFor(name) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function timeAgo(date) {
  if (!date) return '';
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isVideoUrl(url) {
  if (!url) return false;
  return /\.(mp4|mov|webm|ogg|avi|mkv)(\?|$)/i.test(url);
}

const XLogo = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function PostCard({ post }) {
  const acct = accountFor(post);
  const [broken, setBroken] = useState(false);
  const gradient = AVATAR_GRADIENTS[(acct.handle.length) % AVATAR_GRADIENTS.length];
  const created = post.postedAt?.toDate?.() ?? post.createdAt?.toDate?.() ?? null;
  const href = permalink(post);
  const showMedia = post.mediaUrl && !broken && !isVideoUrl(post.mediaUrl);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent('select_content', { content_type: 'social_post', item_id: post.id })}
      className="group relative flex w-[300px] shrink-0 flex-col rounded-2xl border border-white/15 bg-white/[0.07] p-5 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:border-primary-teal/50 hover:bg-white/[0.12] hover:shadow-glow sm:w-[340px]"
    >
      {/* header */}
      <div className="mb-3 flex items-center gap-3">
        {acct.avatar ? (
          <img
            src={acct.avatar}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover shadow-lg ring-1 ring-white/20"
            loading="lazy"
            aria-hidden
          />
        ) : (
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-sm font-bold text-white shadow-lg`}
            aria-hidden
          >
            {initialsFor(acct.name)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-white">{acct.name}</p>
          <p className="truncate text-xs text-slate-400">
            @{acct.handle}
            {created && <span className="text-slate-500"> · {timeAgo(created)}</span>}
          </p>
        </div>
        <XLogo className="h-4 w-4 shrink-0 text-slate-500 transition-colors group-hover:text-white" />
      </div>

      {/* body */}
      <p className="mb-3 line-clamp-5 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
        {post.content}
      </p>

      {showMedia && (
        <div className="mb-3 overflow-hidden rounded-xl border border-white/10">
          <img
            src={post.mediaUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setBroken(true)}
            className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}

      {post.hashtags?.length > 0 && (
        <p className="mb-3 line-clamp-1 text-xs font-medium text-primary-teal">
          {post.hashtags.slice(0, 4).map((h) => `#${h}`).join(' ')}
        </p>
      )}

      {/* footer — verified provenance, not invented metrics */}
      <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Live on X
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition-colors group-hover:text-primary-teal">
          View post
          <span aria-hidden>↗</span>
        </span>
      </div>
    </a>
  );
}

/** One auto-scrolling row. `reverse` flips direction; duplicated for a seamless loop. */
function MarqueeRow({ posts, reverse = false }) {
  if (posts.length === 0) return null;
  const track = [...posts, ...posts]; // duplicate so -50% translate loops seamlessly
  return (
    <div className="group/row relative flex overflow-hidden py-3">
      <div
        className={`flex w-max gap-5 pr-5 ${
          reverse ? 'animate-marquee-reverse' : 'animate-marquee'
        } group-hover/row:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:flex-wrap motion-reduce:justify-center`}
      >
        {track.map((post, i) => (
          <PostCard key={`${post.id}-${i}`} post={post} />
        ))}
      </div>
    </div>
  );
}

export default function SocialWall() {
  const [posts, setPosts] = useState(null); // null = loading, [] = none

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only posts that genuinely shipped to X. Filter visibility client-side
        // to avoid a composite index on (postedToX, visible).
        const snap = await getDocs(
          query(collection(db, 'social_posts'), where('postedToX', '==', true)),
        );
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (p) =>
              p.visible !== false &&
              (p.content || '').trim() &&
              accountFor(p).handle === WALL_HANDLE,
          )
          .sort((a, b) => {
            const at = (a.postedAt ?? a.createdAt)?.toDate?.() ?? new Date(0);
            const bt = (b.postedAt ?? b.createdAt)?.toDate?.() ?? new Date(0);
            return bt - at;
          })
          .slice(0, 18);
        setPosts(list);
      } catch (err) {
        console.error('Failed to load social wall posts:', err);
        if (!cancelled) setPosts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Split into two rows for the opposing-direction marquee effect.
  const [rowA, rowB] = useMemo(() => {
    if (!posts) return [[], []];
    const a = [];
    const b = [];
    posts.forEach((p, i) => (i % 2 === 0 ? a : b).push(p));
    return [a, b];
  }, [posts]);

  const loading = posts === null;
  const hasPosts = !loading && posts.length > 0;
  const singleRow = hasPosts && posts.length < 4;

  return (
    <Section
      id="wall"
      className="relative overflow-hidden border-t border-white/15 bg-primary-navy py-16 md:py-24"
    >
      {/* Higgsfield ambient background, gently drifting (CSS). Fails silent. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <img
          src="/social-wall/ambient-bg.webp"
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          className="h-full w-full object-cover opacity-40 animate-bg-drift motion-reduce:animate-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-navy via-primary-navy/70 to-primary-navy" />
      </div>

      <Container className="relative z-10">
        <div className="mx-auto mb-10 max-w-2xl text-center md:mb-14">
          <p className="mb-3 text-xs font-semibold tracking-widest text-primary-teal">
            STRAIGHT FROM THE FEED
          </p>
          <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">
            Building in public, one post at a time
          </h2>
          <p className="text-slate-300">
            Real posts my content loop wrote, a human approved, and published to X — pulled live,
            not screenshotted. Hover to pause; tap any card to read it on X.
          </p>
        </div>

        {/* Higgsfield-generated focal visual: the content loop that writes these posts */}
        <figure className="mx-auto mb-12 max-w-2xl md:mb-16">
          <div className="group relative overflow-hidden rounded-2xl ring-1 ring-white/15 shadow-2xl shadow-primary-teal/20">
            <div className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-tr from-primary-teal/20 via-transparent to-primary-purple/20 opacity-60" aria-hidden />
            <img
              src="/social-wall/loop-graphic.webp"
              alt="Abstract visualization of the automated content loop that drafts, publishes, and measures these posts"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.closest('figure').style.display = 'none';
              }}
              className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </div>
          <figcaption className="mt-3 text-center text-xs text-slate-400">
            The closed content loop — ideate → draft → human-approve → publish → measure → learn.
          </figcaption>
        </figure>
      </Container>

      {/* Full-bleed marquee (escapes the container for edge-to-edge motion).
          The marquee is the only part gated on real data — never fabricated. */}
      {hasPosts && (
        <div
          className="relative z-10"
          style={{
            maskImage:
              'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
            WebkitMaskImage:
              'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
          }}
        >
          {singleRow ? (
            <MarqueeRow posts={posts} />
          ) : (
            <>
              <MarqueeRow posts={rowA} />
              <MarqueeRow posts={rowB} reverse />
            </>
          )}
        </div>
      )}

      {/* Honest empty / loading state — no invented posts */}
      {!hasPosts && (
        <Container className="relative z-10">
          <div className="mx-auto max-w-md rounded-2xl border border-dashed border-white/20 bg-white/[0.04] px-6 py-8 text-center backdrop-blur-sm">
            <p className="text-sm text-slate-300">
              {loading
                ? 'Loading the latest posts from X…'
                : 'The wall fills in the moment a post ships to X — nothing here is staged, so it starts empty.'}
            </p>
          </div>
        </Container>
      )}

      <Container className="relative z-10">
        <div className="mt-10 text-center">
          <a
            href="https://x.com/techtonic190427"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:border-primary-teal/60 hover:text-primary-teal"
          >
            <XLogo className="h-4 w-4" />
            Follow the build on X
          </a>
        </div>
      </Container>
    </Section>
  );
}
